/**
 * A player's match reports, aggregated (SE-6986, AC1–AC4).
 *
 * Three rules live here, and each is a thing the screen must not get wrong:
 *
 * 1. **Only this player's evaluation.** The query returns whole reports, and
 *    the ticket is explicit that other players in a report are never shown on
 *    a player's page. Filtering is a requirement, not a tidy-up.
 * 2. **One row per MATCH, not per report.** Two scouts watching one fixture
 *    produce two reports, because `createMatchReport` is idempotent per
 *    (match, scout) — so the multi-scout case arrives as separate reports on
 *    the same match, and rendering them as separate rows would tell a reader
 *    the player was watched twice as often as they were.
 * 3. **The averages are over EVALUATIONS, not over matches.** Two scouts who
 *    both scored a fixture are two independent reads, which is the whole point
 *    of watching with two scouts; collapsing them to one would throw away the
 *    second opinion the process exists to produce.
 * @module features/player-match-reports/utils/toPlayerReportRows
 */

import type {
  MatchReportStatus,
  MatchWatchMode,
  PlayerMatchReportsQuery,
} from "@/generated/graphql";

/** One report node as the query returns it. */
type ReportNode =
  PlayerMatchReportsQuery["listMatchReports"]["edges"][number]["node"];

/** One scout's read of this player in one fixture. */
export interface PlayerEvaluationEntry {
  /** Evaluation id. */
  readonly id: string;
  /** The report it belongs to. */
  readonly reportId: string;
  /**
   * Who wrote it — the report's owning scout. Never blank (SE-8016).
   *
   * `MatchReport.scoutUser` is `User!` and `User.displayName` is `String!`, so
   * a read always names a person. This used to be `scoutUser?.email`, the one
   * field on `User` that CAN be null, which is why a report whose scout was
   * present in the payload rendered as "Unknown scout". The server's own chain
   * — Cognito full name, else the email local part, else the email, else
   * "Unnamed user" — is what guarantees the cell is never empty, so there is
   * no client-side fallback here and none is reachable.
   */
  readonly scoutLabel: string;
  /** Performance 0–10, or null when noted but unscored. */
  readonly performanceScore: number | null;
  /** Potential 0–10, or null. */
  readonly potentialScore: number | null;
  /** The scout's note, or null. */
  readonly note: string | null;
  /**
   * Whether the player started, as the scout said — three-state (SE-8016).
   *
   * Null is "the scout has not said", which is not a did-not-start; the panel
   * renders nothing at all for it rather than asserting a bench appearance.
   */
  readonly isStarter: boolean | null;
  /** How the scout watched this fixture, or null when unrecorded. */
  readonly watchMode: MatchWatchMode | null;
  /** Server-derived completeness of the report this read belongs to. */
  readonly status: MatchReportStatus;
  /**
   * How many OTHER players this report covers (SE-8016).
   *
   * Derived, never a constant: the expanded panel says out loud that the rest
   * of the report is not on this page, and a hardcoded number would be right
   * for one report and a lie for every other. Counted with the same `isNoted`
   * predicate that admits this player, so the two cannot disagree — and by
   * distinct player, because one player carries at most one evaluation per
   * report but the count must survive that ever changing.
   */
  readonly otherPlayerCount: number;
  /** When the scout first attended to this player, or null. */
  readonly notedAt: string | null;
  /** True when this read is older than the staleness threshold (AC4). */
  readonly isStale: boolean;
}

/** One fixture this player was watched in. */
export interface PlayerReportRow {
  /** The match id — the row's identity, since two reports can share it. */
  readonly matchId: string;
  /** "Sporting CP vs Benfica". */
  readonly title: string;
  /** Competition, or null. */
  readonly competition: string | null;
  /** Civil date, `YYYY-MM-DD`. */
  readonly date: string;
  /** Venue, or null. */
  readonly venue: string | null;
  /**
   * Every read of this player in this fixture, owner first (AC3).
   *
   * "Owner" is the FIRST scout to evaluate, per the ticket. Ordered by when
   * each scout actually attended to the player, not by when the report row was
   * created — a report created with the squad and touched days later would
   * otherwise claim ownership it did not earn.
   */
  readonly evaluations: readonly PlayerEvaluationEntry[];
  /** True when any read of this fixture is stale. */
  readonly hasStaleEvaluation: boolean;
}

/** The aggregation cards (AC2). */
export interface PlayerReportStats {
  /** Fixtures this player was watched in. */
  readonly matchesWatched: number;
  /** Mean Performance across every scored read, or null when none exists. */
  readonly averagePerformance: number | null;
  /** Mean Potential across every scored read, or null. */
  readonly averagePotential: number | null;
  /**
   * Reads that exist at all — the denominator each average is measured against.
   *
   * NOT the same as `matchesWatched`: one fixture watched by two scouts is one
   * match and two reads.
   */
  readonly evaluationCount: number;
  /**
   * How many reads actually carry a Performance score (SE-7237).
   *
   * The mean deliberately skips unscored reads, which means a card can present
   * a hard number resting on a handful of the reads on file — on dev, Potential
   * is blank for most evaluations while Performance is filled for nearly all,
   * so "Avg potential 6.5" could be four reads out of twenty-three. Carrying
   * the contributing count lets the card say so instead of implying the average
   * speaks for every read: a number must never look more certain than it is.
   */
  readonly performanceFrom: number;
  /** How many reads actually carry a Potential score (SE-7237). */
  readonly potentialFrom: number;
  /** How many reads are old enough to want redoing. */
  readonly staleCount: number;
}

/**
 * How old an evaluation may be before the page asks for a fresh one (AC4).
 *
 * Six months, hard-coded behind this name in V1 because **no document defines
 * the value** — the ticket says as much and leaves product to confirm it. It is
 * a named constant rather than a literal so that confirming it is a one-line
 * change with one place to look, and so that a reader can see the number is a
 * decision rather than an accident.
 */
export const STALE_AFTER_MONTHS = 6;

/**
 * Whether an evaluation is old enough to flag.
 *
 * Compared in whole UTC months rather than in days: "six months old" is how a
 * scout talks about it, and a day-count would flip the flag on a different date
 * for every reader depending on their offset.
 * @param notedAt - When the scout attended to the player, or null.
 * @param now - The instant to compare against, injected so this stays pure.
 * @returns True when the evaluation is older than the threshold.
 */
export const isEvaluationStale = (
  notedAt: string | null | undefined,
  now: Date
): boolean => {
  if (notedAt === null || notedAt === undefined) return false;

  const noted = new Date(notedAt);
  if (Number.isNaN(noted.getTime())) return false;

  const months =
    (now.getUTCFullYear() - noted.getUTCFullYear()) * 12 +
    (now.getUTCMonth() - noted.getUTCMonth());

  // A same-day-of-month boundary counts as not yet stale, so the flag appears
  // the day AFTER six months rather than on the anniversary itself.
  return (
    months > STALE_AFTER_MONTHS ||
    (months === STALE_AFTER_MONTHS && now.getUTCDate() > noted.getUTCDate())
  );
};

/**
 * How many players OTHER than this one the report covers (SE-8016).
 *
 * The expanded panel tells the reader, in words, that the rest of the report is
 * never shown on this page — which only helps if the number is true. Counted
 * off the payload with the same `isNoted` predicate that decides this player is
 * in the report at all: a count drawn from a different population than the one
 * the sentence describes is a subtler version of hardcoding it.
 *
 * Distinct player ids rather than evaluation rows, and rows with no player
 * (an unidentified placeholder, or one outside the caller's scope) are not
 * counted — the sentence says "players", and a row nobody can name is not one
 * the reader could go and look at.
 * @param report - The report node.
 * @param playerId - The player whose page this is.
 * @returns The count of other noted players, never negative.
 */
const countOtherNotedPlayers = (report: ReportNode, playerId: string): number =>
  new Set(
    report.playerEvaluations
      .filter(
        evaluation =>
          evaluation.isNoted &&
          evaluation.player != null &&
          evaluation.player.id !== playerId
      )
      .map(evaluation => evaluation.player?.id)
  ).size;

/**
 * Builds one fixture's rows for a player.
 * @param reports - Report nodes, from the `playerIds`-filtered query.
 * @param playerId - The player whose page this is.
 * @param now - The instant staleness is measured against.
 * @returns One row per fixture, newest first.
 */
export const toPlayerReportRows = (
  reports: readonly ReportNode[],
  playerId: string,
  now: Date
): readonly PlayerReportRow[] => {
  const entriesByMatch = reports.reduce<
    ReadonlyMap<
      string,
      { readonly report: ReportNode; readonly entry: PlayerEvaluationEntry }[]
    >
  >((byMatch, report) => {
    // ONLY this player. The ticket forbids showing anyone else's evaluation on
    // a player's page, and the query has no way to narrow it server-side.
    const mine = report.playerEvaluations.filter(
      evaluation => evaluation.player?.id === playerId && evaluation.isNoted
    );
    if (mine.length === 0) return byMatch;

    const matchId = report.match?.id;
    if (matchId === undefined) return byMatch;

    const existing = byMatch.get(matchId) ?? [];

    // The rest of the report, counted here rather than in the View: the panel
    // states this number to the reader, so it is derived from the payload the
    // row was built from and never from a literal.
    const otherPlayerCount = countOtherNotedPlayers(report, playerId);

    return new Map([
      ...byMatch,
      [
        matchId,
        [
          ...existing,
          ...mine.map(evaluation => ({
            report,
            entry: {
              id: evaluation.id,
              reportId: report.id,
              // `displayName`, not `email`: the former is non-null by contract
              // and the latter is not, which is the whole of why this cell used
              // to read "Unknown scout" (SE-8016).
              scoutLabel: report.scoutUser.displayName,
              performanceScore: evaluation.performanceScore ?? null,
              potentialScore: evaluation.potentialScore ?? null,
              note: evaluation.note ?? null,
              isStarter: evaluation.isStarter ?? null,
              watchMode: report.watchMode ?? null,
              status: report.status,
              otherPlayerCount,
              notedAt: evaluation.notedAt ?? null,
              isStale: isEvaluationStale(evaluation.notedAt, now),
            },
          })),
        ],
      ],
    ]);
  }, new Map());

  const rows = [...entriesByMatch.entries()].map(([matchId, entries]) => {
    const first = entries[0].report;
    const home = first.match?.homeTeam?.name ?? "TBC";
    const away = first.match?.awayTeam?.name ?? "TBC";

    // Owner first: the FIRST scout to attend to the player. An unnoted-then-
    // noted row sorts by when it was noted, so ownership follows the work.
    const ordered = [...entries]
      .map(item => item.entry)
      .sort((left, right) => {
        if (left.notedAt === null && right.notedAt === null) return 0;
        if (left.notedAt === null) return 1;
        if (right.notedAt === null) return -1;

        return left.notedAt.localeCompare(right.notedAt);
      });

    return {
      matchId,
      title: `${home} vs ${away}`,
      competition: first.match?.competitionName ?? null,
      date: first.match?.scheduledDate ?? "",
      venue: first.match?.venue ?? null,
      evaluations: ordered,
      hasStaleEvaluation: ordered.some(entry => entry.isStale),
    };
  });

  // Newest fixture first: a player page is read to answer "how have they looked
  // lately", and the most recent view is the one that answers it.
  return [...rows].sort((left, right) => right.date.localeCompare(left.date));
};

/**
 * Builds the aggregation cards.
 * @param rows - The player's fixture rows.
 * @returns Matches watched and the two averages.
 */
export const toPlayerReportStats = (
  rows: readonly PlayerReportRow[]
): PlayerReportStats => {
  const evaluations = rows.flatMap(row => row.evaluations);

  /**
   * Mean of the scores that EXIST, plus how many of them there were.
   *
   * An unscored read is not a zero — the scout noted the player and never
   * rated them — so it is excluded rather than dragging the average down. That
   * exclusion is what makes the contributing count worth returning alongside
   * the mean (SE-7237): the card can then disclose the coverage the average
   * rests on rather than presenting a sparse mean as if every read agreed.
   * @param pick - Reads one score off an entry.
   * @returns The mean rounded to one decimal (null when nothing was scored)
   *   and the number of reads that contributed to it.
   */
  const meanOf = (
    pick: (entry: PlayerEvaluationEntry) => number | null
  ): { readonly mean: number | null; readonly from: number } => {
    const scores = evaluations
      .map(pick)
      .filter((score): score is number => score !== null);
    if (scores.length === 0) return { mean: null, from: 0 };

    return {
      mean:
        Math.round(
          (scores.reduce((total, score) => total + score, 0) / scores.length) *
            10
        ) / 10,
      from: scores.length,
    };
  };

  const performance = meanOf(entry => entry.performanceScore);
  const potential = meanOf(entry => entry.potentialScore);

  return {
    // Fixtures, not reads: watching one match with two scouts is one match.
    matchesWatched: rows.length,
    averagePerformance: performance.mean,
    averagePotential: potential.mean,
    // Reads, not fixtures — the denominator the two averages are drawn from.
    evaluationCount: evaluations.length,
    performanceFrom: performance.from,
    potentialFrom: potential.from,
    staleCount: evaluations.filter(entry => entry.isStale).length,
  };
};
